import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(false))
      .finally(() => setReady(true));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("access_token", data.access_token);
    localStorage.setItem("user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name, office_slug) => {
    const payload = { email, password, name };
    if (office_slug) payload.office_slug = office_slug;
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("access_token", data.access_token);
    localStorage.setItem("user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Logout server-side is best-effort: even if it fails (offline, expired token),
      // we still want to clear local state below.
      console.warn("Logout API call failed, clearing local state anyway:", e?.message);
    }
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setUser(false);
  };

  const refresh = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    localStorage.setItem("user", JSON.stringify(data));
    return data;
  };

  // Memoize context value to avoid forcing all consumers to re-render
  // whenever AuthProvider re-renders (login/register/logout/refresh are stable refs
  // via closure; user/ready are the actual signals).
  const value = useMemo(
    () => ({ user, ready, login, register, logout, refresh }),
    [user, ready]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
